<template>
  <div class="space-y-4">
    <h2>{{ t('payment') }}</h2>

    <div class="grid w-4/5 grid-cols-2 gap-x-6 lg:w-2/3">
      <LabeledText :label="t('openRentalFee')">
        <span v-currency="Math.max(openRentalFee - unpaidDeposit, 0)" />
      </LabeledText>
      <LabeledText :label="t('deposit')">
        <span v-currency="booking.priceCalculation.deposit" />
      </LabeledText>
    </div>

    <template v-if="isSettled">
      <p class="text-green-700">{{ t('nothingOpen') }}</p>
      <div class="flex justify-end">
        <ButtonNext @click.prevent="goToNext">{{ t('next') }}</ButtonNext>
      </div>
    </template>

    <template v-else>
      <p>{{ t('paymentLinkExplanation') }}</p>
      <StatusBanner v-if="isWaitingForPayment" variant="info">
        {{ t('waitingForPayment') }}
      </StatusBanner>
      <div class="flex justify-end">
        <CVButton :is-loading="isSending" @click.prevent="handleSendPaymentLink">
          <SendIcon class="size-4" />
          {{ isWaitingForPayment ? t('resendPaymentLink') : t('sendPaymentLink') }}
        </CVButton>
      </div>
      <p class="text-sm text-gray-600">{{ t('problemsHint') }}</p>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIntervalFn } from '@vueuse/core';
import { SendIcon } from 'lucide-vue-next';
import LabeledText from '@/components/LabeledText.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useProcessNavigation } from '@/hooks/use-process-step-tracking';
import { useSendExternalPaymentLink } from '@/queries/use-external-handover';

const props = defineProps<{
  booking: Booking;
}>();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();

const { t } = useI18n();
const { goToNext } = useProcessNavigation();

// Same amounts as the agent payment step; the server sends the link for exactly these.
const openRentalFee = computed(() => props.booking.currentBalance.CUSTOMER ?? 0);
const unpaidDeposit = computed(() => Math.max(0, props.booking.unpaidDeposit ?? 0));
const deposit = computed(() =>
  Math.min(
    props.booking.priceCalculation.deposit,
    props.booking.blockedCreditCardAmount || Number.MAX_VALUE,
  ),
);
const totalAmount = computed(
  () => (props.booking.collectDeposit ? 0 : deposit.value) + Math.max(openRentalFee.value, 0),
);
const isSettled = computed(() => {
  if (totalAmount.value === 0) return true;
  if (props.booking.collectDeposit) return openRentalFee.value <= 0;
  if (!props.booking.blockedCreditCardDate) return false;
  return (props.booking.blockedCreditCardAmount ?? 0) >= totalAmount.value - 0.001;
});

const isWaitingForPayment = ref(false);
const { mutateAsync: sendPaymentLink, isPending: isSending } = useSendExternalPaymentLink();

const handleSendPaymentLink = async () => {
  await sendPaymentLink(props.booking.id);
  isWaitingForPayment.value = true;
};

// Poll until the booking is settled: the customer pays on their own phone, or the station
// confirms the deposit manually ("Sonderfall Kaution") in the booking view.
useIntervalFn(() => {
  if (!isSettled.value) emit('refresh');
}, 5000);
</script>

<i18n lang="json">
{
  "en": {
    "payment": "Payment",
    "openRentalFee": "Open rental fee",
    "deposit": "Deposit",
    "nothingOpen": "Everything is paid and the deposit is secured.",
    "paymentLinkExplanation": "The customer pays the open amount and deposit via a payment link sent to their email address.",
    "sendPaymentLink": "Send payment link",
    "resendPaymentLink": "Send payment link again",
    "waitingForPayment": "Waiting for the customer to complete the payment. This page updates automatically.",
    "problemsHint": "If the customer cannot pay, please call the station.",
    "next": "Next"
  },
  "de": {
    "payment": "Zahlung",
    "openRentalFee": "Noch offener Mietbetrag",
    "deposit": "Kaution",
    "nothingOpen": "Alles ist bezahlt und die Kaution ist hinterlegt.",
    "paymentLinkExplanation": "Der Kunde bezahlt den offenen Betrag und die Kaution über einen Zahlungslink, der an seine E-Mail-Adresse geht.",
    "sendPaymentLink": "Zahlungslink senden",
    "resendPaymentLink": "Zahlungslink erneut senden",
    "waitingForPayment": "Warte darauf, dass der Kunde die Zahlung abschließt. Diese Seite aktualisiert sich automatisch.",
    "problemsHint": "Wenn der Kunde nicht bezahlen kann, ruf bitte die Station an.",
    "next": "Weiter"
  },
  "es": {
    "payment": "Pago",
    "openRentalFee": "Importe de alquiler pendiente",
    "deposit": "Fianza",
    "nothingOpen": "Todo está pagado y la fianza está garantizada.",
    "paymentLinkExplanation": "El cliente paga el importe pendiente y la fianza con un enlace de pago que recibe por correo.",
    "sendPaymentLink": "Enviar enlace de pago",
    "resendPaymentLink": "Volver a enviar el enlace de pago",
    "waitingForPayment": "Esperando a que el cliente complete el pago. Esta página se actualiza sola.",
    "problemsHint": "Si el cliente no puede pagar, llama a la estación.",
    "next": "Siguiente"
  }
}
</i18n>
